<template>
  <q-layout>
    <q-page-container style="max-height: 100vh; overflow: auto; background-color: white;">
      <q-page>
        <div class="row justify-center q-pt-md">
          <img src="fd-logo.svg" style="" width="450" />
        </div>

        <div class="row justify-around items-center">
          <div class="col-4">
            <div class="row justify-center">
              <img src="hidoe-logo.jpg" width="250" />
            </div>
          </div>

          <div class="col-4">
            <div v-if="pageStatus === 'Reset Password'">
              <div>
                <h4 class="text-center">Reset Password</h4>
                <q-form
                  @submit="onSubmitNewPassword"
                  class="q-gutter-md"
                >
                  <div class="row" style="font-size: 1vw">
                    <p>Email Address: </p> &ensp;
                    <p class="text-weight-bold">{{ emailAddress }}</p>
                  </div>

                  <div style="display: flex; alignItems: baseline">
                    <div>New Password *</div>
                    <q-space />
                    <q-input
                      v-model="password"
                      filled
                      style="width: 75%;"
                      maxlength="256"
                      :type="passwordVisibility ? 'password' : 'text'"
                      :rules="[
                        (val) =>
                          validatePassword(val) || 'Password must meet all criteria.'
                      ]"
                      :disable="disabledFields"
                    >
                      <template v-slot:append>
                        <q-icon
                          :name="passwordVisibility ? 'visibility_off' : 'visibility'"
                          class="cursor-pointer"
                          @click="passwordVisibility = !passwordVisibility"
                        />
                      </template>
                    </q-input>
                  </div>

                  <div class="grey-background q-pa-sm">
                    <div class="q-mb-sm text-weight-bold">Password Criteria:</div>
                    <div>
                      <q-icon
                        :name="validPassword.length ? 'check_circle' : 'cancel'"
                        :color="validPassword.length ? 'positive' : 'negative'"
                      />
                      Must contain at least 12 characters.
                    </div>
                    <div>
                      <q-icon
                        :name="validPassword.lowercase ? 'check_circle' : 'cancel'"
                        :color="validPassword.lowercase ? 'positive' : 'negative'"
                      />
                      Must contain at least one lowercase character.
                    </div>
                    <div>
                      <q-icon
                        :name="validPassword.uppercase ? 'check_circle' : 'cancel'"
                        :color="validPassword.uppercase ? 'positive' : 'negative'"
                      />
                      Must contain at least one uppercase character.
                    </div>
                    <div>
                      <q-icon
                        :name="validPassword.number ? 'check_circle' : 'cancel'"
                        :color="validPassword.number ? 'positive' : 'negative'"
                      />
                      Must contain at least one numeric character.
                    </div>
                    <div>
                      <q-icon
                        :name="validPassword.symbol ? 'check_circle' : 'cancel'"
                        :color="validPassword.symbol ? 'positive' : 'negative'"
                      />
                      Must contain at least one non-alphanumeric character: ^ $ * . [ ] { } ( ) ? " ! @ # % &amp; / \ , &gt; &lt; ' : ; | _ ~ `
                    </div>
                  </div>

                  <div style="display: flex; alignItems: baseline">
                    <div>Confirm Password *</div>
                    <q-space />
                    <q-input
                      v-model="confirmPassword"
                      filled
                      style="width: 75%;"
                      maxlength="256"
                      :type="confirmPasswordVisibility ? 'password' : 'text'"
                      :rules="[
                        (val) =>
                          (val && val === password) || 'Must match password.'
                      ]"
                      :disable="disabledFields"
                    >
                      <template v-slot:append>
                        <q-icon
                          :name="confirmPasswordVisibility ? 'visibility_off' : 'visibility'"
                          class="cursor-pointer"
                          @click="confirmPasswordVisibility = !confirmPasswordVisibility"
                        />
                      </template>
                    </q-input>
                  </div>

                  <br />

                  <div class="row justify-center" style="margin: 0;">
                    <q-btn label="Submit" no-caps type="submit" color="primary" :disable="disabledFields"/>
                  </div>
                </q-form>
              </div>
            </div>

            <div v-if="pageStatus === 'Not Found'">
              <h4 class="text-center">Reset Password</h4>
              <p style="font-size: 1vw">Your request to reset your password has expired or the link has already been used. Try resetting your password again.</p>
              <div class="row justify-center q-pt-lg" style="margin: 0;">
                <q-btn label="Return to Login" no-caps color="primary" @click="redirectToLogin()"/>
              </div>
            </div>

            <div v-if="pageStatus === 'Loading'">
              <div class="row justify-center">
                <q-spinner
                  color="primary"
                  size="10em"
                />
              </div>
            </div>
          </div>

          <div class="col-4">
            <div class="row justify-center">
              <img class="grey-background q-pa-lg" src="eWorld-Logo2.svg" width="300"/>
            </div>
          </div>
        </div>
      </q-page>
    </q-page-container>
  </q-layout>
</template>

<script>
import { ref, onBeforeMount } from 'vue';
import router from "../../router/index.js";
import NotificationHelper from "../helper/NotificationHelper.js";
import EmailService from "../service/EmailService.js";
import { auth } from '../firebase/config.js';
import { verifyPasswordResetCode, confirmPasswordReset } from 'firebase/auth';

export default {
  name: "ResetPassword",
  setup() {
    const pageStatus = ref("Loading");
    const emailAddress = ref("");
    const oobCode = ref("");
    const disabledFields = ref(false);

    const password = ref("");
    const passwordVisibility = ref(true);
    const confirmPassword = ref("");
    const confirmPasswordVisibility = ref(true);

    onBeforeMount(async () => {
      const urlParams = new URLSearchParams(window.location.search);
      oobCode.value = urlParams.get("oobCode");

      try {
        emailAddress.value = await verifyPasswordResetCode(auth, oobCode.value);
        pageStatus.value = "Reset Password";
      } catch {
        pageStatus.value = "Not Found";
      }
    });

    const validPassword = ref({
      length: false,
      lowercase: false,
      uppercase: false,
      number: false,
      symbol: false
    });

    function validatePassword() {
      // Test length: At least 12 characters long
      validPassword.value.length = password.value.length >= 12;

      // Test lowercase: At least one lowercase letter
      validPassword.value.lowercase = /^(?=.*[a-z])/.test(password.value);

      // Test uppercase: At least one uppercase letter
      validPassword.value.uppercase = /^(?=.*[A-Z])/.test(password.value);

      // Test number: At least one number
      validPassword.value.number = /^(?=.*[0-9])/.test(password.value);

      // Test symbol: At least one special character
      validPassword.value.symbol = /^(?=.*[\^\$*\.\[\]\{\}\(\)\?\"!@#%&\/\\,><':;|_~`])/.test(password.value);

      return (
        validPassword.value.length &&
        validPassword.value.lowercase &&
        validPassword.value.uppercase &&
        validPassword.value.number &&
        validPassword.value.symbol
      );
    }

    async function onSubmitNewPassword() {
      try {
        disabledFields.value = true;
        await confirmPasswordReset(auth, oobCode.value, password.value);
        NotificationHelper.createNotification(
          "Password changed successfully. You can now sign in with your new password.",
          {
            timeout: 10000,
            type: "positive"
          }
        );
        redirectToLogin();
      } catch (error) {
        disabledFields.value = false;
        if (error.code === "auth/password-does-not-meet-requirements") {
          NotificationHelper.createErrorNotification("Password does not meet requirements.");
        } else {
          EmailService.sendErrorEmail("ResetPasswordPage.vue - onSubmitNewPassword", error);
          NotificationHelper.createErrorNotification("Error encountered. Please try again.");
        }
      }
    }

    function redirectToLogin() {
      router.push('/login');
    }

    return {
      // variable properties
      pageStatus,
      emailAddress,
      disabledFields,
      password,
      passwordVisibility,
      confirmPassword,
      confirmPasswordVisibility,
      validPassword,

      //functions
      validatePassword,
      onSubmitNewPassword,
      redirectToLogin
    };
  }
}
</script>

<style scoped>
.grey-background {
  background-color: #efefef;
  border-radius: 0.5rem;
}
</style>
